import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { MessageCircle, RotateCcw, Send } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import Button from '../../components/ui/Button';
import {
  loadWhatsappMockThreads,
  resetWhatsappMockThreads,
  saveWhatsappMockThreads,
  type MockThread,
} from '../../lib/whatsappMockData';
import { stageLabel, suggestionsForThread } from '../../lib/whatsappMockSuggestions';

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || '')
    .join('')
    .toUpperCase();
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * In-app WhatsApp messaging lab — uses the same agency session as the main app.
 * Sends stay local (localStorage); suggestions map to real messaging API paths.
 */
export default function AgencyWhatsAppMock() {
  const { user } = useAuthStore();
  const isOrgAdmin = ['OWNER', 'ADMIN'].includes(user?.roleInOrg || '');
  const [threads, setThreads] = useState<MockThread[]>(() => loadWhatsappMockThreads());
  const [activeId, setActiveId] = useState<string | null>(() => loadWhatsappMockThreads()[0]?.id ?? null);
  const [draft, setDraft] = useState('');
  const [banner, setBanner] = useState('Local mock lab — Send does not call Meta; messages stay in this browser.');
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const active = useMemo(
    () => threads.find((t) => t.id === activeId) || null,
    [threads, activeId]
  );

  const suggestions = useMemo(
    () => (active ? suggestionsForThread(active) : []),
    [active]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter(
      (t) =>
        t.kolName.toLowerCase().includes(q) ||
        t.campaignName.toLowerCase().includes(q) ||
        t.phone.includes(q)
    );
  }, [threads, search]);

  const apiRefs = useMemo(() => {
    const seen = new Set<string>();
    const list: { method: string; path: string; note?: string }[] = [];
    for (const s of suggestions) {
      for (const api of s.apis) {
        const key = `${api.method} ${api.path}`;
        if (seen.has(key)) continue;
        seen.add(key);
        list.push(api);
      }
    }
    return list;
  }, [suggestions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [active?.messages.length, activeId]);

  const persist = (next: MockThread[]) => {
    setThreads(next);
    saveWhatsappMockThreads(next);
  };

  const sendMessage = (text: string, eventKey?: string | null) => {
    const body = text.trim();
    if (!active || !body) return;
    const now = new Date().toISOString();
    const next = threads.map((t) => {
      if (t.id !== active.id) return t;
      return {
        ...t,
        lastPreview: body.slice(0, 48),
        messages: [
          ...t.messages,
          {
            id: `local-${Date.now()}`,
            direction: 'out' as const,
            text: body,
            at: now,
            eventKey: eventKey || undefined,
          },
          {
            id: `sys-${Date.now()}`,
            direction: 'system' as const,
            text: `Mock send as ${user?.email || 'user'} (${user?.role}/${user?.roleInOrg || '—'}) · WHATSAPP · ${
              eventKey || 'free-text'
            } · local only`,
            at: now,
          },
        ],
      };
    });
    persist(next);
    setDraft('');
    setBanner(
      'Message stored locally. Production would POST /api/whatsapp-inbox/threads/:id/messages with your session.'
    );
  };

  const handleReset = () => {
    const seed = resetWhatsappMockThreads();
    setThreads(seed);
    setActiveId(seed[0]?.id ?? null);
    setDraft('');
    setBanner('Mock chats reset to seed data.');
  };

  if (!isOrgAdmin) {
    return <Navigate to="/agency/dashboard" replace />;
  }

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[2rem] font-semibold tracking-tight text-glass-text flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-accent2" />
            WhatsApp mock lab
          </h1>
          <p className="mt-1 text-sm text-glass-text-2 max-w-2xl leading-relaxed">
            Practice status-aware reminders against demo KOL stages. Uses your current agency login
            (no separate mock host). Pair with{' '}
            <Link to="/agency/api-docs" className="text-accent2 hover:underline">
              API docs
            </Link>{' '}
            for real endpoint reference.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs rounded-full border border-glass bg-glass-2 px-2.5 py-1 text-glass-text-2">
            {user?.fullName || user?.email} · {user?.role}
            {user?.roleInOrg ? ` · ${user.roleInOrg}` : ''}
          </span>
          <Button size="sm" variant="outline" onClick={handleReset}>
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Reset chats
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200/90">
        {banner}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)_300px] gap-3 min-h-[70vh]">
        {/* Threads */}
        <aside className="rounded-xl border border-glass bg-glass-1/70 flex flex-col min-h-[320px] overflow-hidden">
          <div className="p-3 border-b border-glass space-y-2">
            <h2 className="text-sm font-semibold text-glass-text">Chats</h2>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search KOL or campaign…"
              className="w-full rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text placeholder:text-glass-text-3"
            />
          </div>
          <ul className="flex-1 overflow-y-auto glass-scroll">
            {filtered.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setActiveId(t.id)}
                  className={`w-full grid grid-cols-[2.5rem_1fr_auto] gap-2 items-center px-3 py-3 text-left border-b border-glass/60 hover:bg-glass-2 ${
                    t.id === activeId ? 'bg-glass-3' : ''
                  }`}
                >
                  <span className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-600 to-emerald-400 text-white text-xs font-bold grid place-items-center">
                    {initials(t.kolName)}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-glass-text truncate">{t.kolName}</span>
                    <span className="block text-xs text-glass-text-2 truncate">
                      {t.campaignName} · {t.lastPreview}
                    </span>
                  </span>
                  <span className="text-[0.65rem] uppercase tracking-wide rounded-full border border-glass px-1.5 py-0.5 text-accent2">
                    {stageLabel(t)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Chat */}
        <section className="rounded-xl border border-glass bg-[#0b141a] flex flex-col min-h-[420px] overflow-hidden">
          <div className="px-4 py-3 border-b border-glass bg-glass-1/80">
            {active ? (
              <>
                <h3 className="text-base font-semibold text-glass-text">
                  {active.kolName}{' '}
                  <span className="text-sm font-normal text-glass-text-2">{active.phone}</span>
                </h3>
                <p className="text-xs text-glass-text-2">
                  {active.campaignName} · {active.brandName}
                </p>
              </>
            ) : (
              <p className="text-sm text-glass-text-2">Select a KOL conversation</p>
            )}
          </div>
          <div className="flex-1 overflow-y-auto glass-scroll p-4 flex flex-col gap-2">
            {active?.messages.map((m) =>
              m.direction === 'system' ? (
                <div
                  key={m.id}
                  className="self-center max-w-[90%] rounded-lg bg-glass-2 px-3 py-1.5 text-xs text-glass-text-2 text-center"
                >
                  {m.text}
                </div>
              ) : (
                <div
                  key={m.id}
                  className={`max-w-[75%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                    m.direction === 'out'
                      ? 'self-end bg-[#005c4b] text-white rounded-tr-sm'
                      : 'self-start bg-glass-2 text-glass-text rounded-tl-sm'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  <div className="mt-1 text-[0.65rem] text-right opacity-70">
                    {formatTime(m.at)}
                    {m.eventKey ? ` · ${m.eventKey}` : ''}
                  </div>
                </div>
              )
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-3 border-t border-glass bg-glass-1/80 grid grid-cols-[1fr_auto] gap-2">
            <textarea
              rows={2}
              value={draft}
              disabled={!active}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(draft);
                }
              }}
              placeholder={active ? 'Type a WhatsApp message…' : 'Select a chat first'}
              className="rounded-lg border border-glass bg-glass-2 px-3 py-2 text-sm text-glass-text resize-none disabled:opacity-50"
            />
            <Button
              className="self-end"
              disabled={!active || !draft.trim()}
              onClick={() => sendMessage(draft)}
            >
              <Send className="w-4 h-4 mr-1.5" />
              Send
            </Button>
          </div>
        </section>

        {/* Suggestions */}
        <aside className="rounded-xl border border-glass bg-glass-1/70 flex flex-col min-h-[320px] overflow-hidden">
          <div className="p-3 border-b border-glass">
            <h2 className="text-sm font-semibold text-glass-text">Status &amp; suggestions</h2>
          </div>
          <div className="flex-1 overflow-y-auto glass-scroll p-3 space-y-3">
            {!active ? (
              <p className="text-sm text-glass-text-2">
                Pick a thread to see campaign progress and recommended reminders.
              </p>
            ) : (
              <>
                <div className="rounded-lg border border-glass bg-glass-2/50 p-3 text-sm">
                  <p className="font-medium text-glass-text mb-2">{stageLabel(active)} stage</p>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                    <dt className="text-glass-text-2">Invitation</dt>
                    <dd className="font-mono">{active.invitationStatus || '—'}</dd>
                    <dt className="text-glass-text-2">Script</dt>
                    <dd className="font-mono">{active.scriptStatus || '—'}</dd>
                    <dt className="text-glass-text-2">Submission</dt>
                    <dd className="font-mono">{active.submissionStatus || '—'}</dd>
                    <dt className="text-glass-text-2">Payment</dt>
                    <dd className="font-mono">{active.paymentStatus || '—'}</dd>
                    <dt className="text-glass-text-2">Pipeline</dt>
                    <dd className="font-mono">{active.scriptPipelinePhase || '—'}</dd>
                  </dl>
                </div>

                {suggestions.map((s) => (
                  <article key={s.id} className="rounded-lg border border-glass bg-glass-2/40 p-3 space-y-2">
                    <h4 className="text-sm font-semibold text-glass-text">{s.title}</h4>
                    <p className="text-xs text-glass-text-2 leading-relaxed">{s.why}</p>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[0.65rem] font-mono rounded-full border border-accent2/30 text-accent2 px-2 py-0.5">
                        {s.eventKey || 'manual'}
                      </span>
                      <span className="text-[0.65rem] rounded-full border border-glass text-glass-text-2 px-2 py-0.5">
                        WHATSAPP
                      </span>
                    </div>
                    <pre className="whitespace-pre-wrap text-xs rounded-md bg-glass-1 border border-glass p-2 text-glass-text">
                      {s.body}
                    </pre>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => setDraft(s.body)}
                      >
                        Insert
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => sendMessage(s.body, s.eventKey)}
                      >
                        Send now
                      </Button>
                    </div>
                  </article>
                ))}

                <div className="rounded-lg border border-dashed border-glass p-3">
                  <h3 className="text-[0.7rem] uppercase tracking-wide text-glass-text-2 mb-2">
                    Linked API (from docs catalog)
                  </h3>
                  <ul className="space-y-1.5">
                    {apiRefs.map((api) => (
                      <li key={`${api.method}-${api.path}`} className="text-xs leading-snug">
                        <code className="text-emerald-400/90">
                          {api.method} {api.path}
                        </code>
                        {api.note ? (
                          <span className="text-glass-text-2"> — {api.note}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
